import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import {
    FormContainer,
    Form,
    Fieldset,
    Label,
    TextInput,
    Dropdown,
    MessageBox,
    UpdateButton,
    ResetButton,
    Legend,
} from '../../../components/FormElements';
import { StyledMicroButton } from '../../../components/Styled';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import { getReturnUrl } from '../../../utils';

const SmsTemplatesUpdate = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [responseStatus, setResponseStatus] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [purpose, setPurpose] = useState('');
    const [smsTemplateId, setSmsTemplateId] = useState('');
    const [template, setTemplate] = useState('');
    const [waTemplateId, setWaTemplateId] = useState('');
    const [waTemplate, setWaTemplate] = useState('');
    const [telegramTemplateId, setTelegramTemplateId] = useState('');
    const [telegramTemplate, setTelegramTemplate] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [pushTitle, setPushTitle] = useState('');
    const [pushText, setPushText] = useState('');
    const [status, setStatus] = useState('Active');
    const [waStatus, setWaStatus] = useState('Active');
    const [emailStatus, setEmailStatus] = useState('Active');
    const [telegramStatus, setTelegramStatus] = useState('Active');
    const [pushStatus, setPushStatus] = useState('Active');
    const [originalFormData, setOriginalFormData] = useState({});
    const { id } = useParams();

    const resetResponseData = () => {
        setIsLoading(false);
        setResponseStatus('');
        setResponseMessage('');
    };

    const resetForm = () => {
        const {
            purpose,
            smsTemplateId,
            template,
            waTemplateId,
            waTemplate,
            telegramTemplateId,
            telegramTemplate,
            emailSubject,
            emailBody,
            pushTitle,
            pushText,
            status,
            waStatus,
            emailStatus,
            telegramStatus,
            pushStatus,
        } = originalFormData;
        setPurpose(purpose);
        setSmsTemplateId(smsTemplateId);
        setTemplate(template);
        setWaTemplateId(waTemplateId);
        setWaTemplate(waTemplate);
        setTelegramTemplateId(telegramTemplateId);
        setTelegramTemplate(telegramTemplate);
        setEmailSubject(emailSubject);
        setEmailBody(emailBody);
        setPushTitle(pushTitle);
        setPushText(pushText);
        setStatus(status);
        setWaStatus(waStatus);
        setEmailStatus(emailStatus);
        setTelegramStatus(telegramStatus);
        setPushStatus(pushStatus);
    };

    const readData = useCallback(() => {
        setIsLoading(true);
        setResponseStatus('');
        setResponseMessage('');
        API.get(`/configurations/sms-templates/${id}`)
            .then((response) => {
                const { status, message, data } = response.data;
                if (status === API_RESPONSE_TYPES.FAILURE) {
                    setResponseStatus(status);
                    setResponseMessage(message);
                } else {
                    const {
                        purpose,
                        smsTemplateId,
                        template,
                        waTemplateId,
                        waTemplate,
                        telegramTemplateId,
                        telegramTemplate,
                        emailSubject,
                        emailBody,
                        pushTitle,
                        pushText,
                        status,
                        waStatus,
                        emailStatus,
                        telegramStatus,
                        pushStatus,
                    } = data[0];
                    setPurpose(purpose);
                    setSmsTemplateId(smsTemplateId);
                    setTemplate(template);
                    setWaTemplateId(waTemplateId);
                    setWaTemplate(waTemplate);
                    setTelegramTemplateId(telegramTemplateId);
                    setTelegramTemplate(telegramTemplate);
                    setEmailSubject(emailSubject);
                    setEmailBody(emailBody);
                    setPushTitle(pushTitle);
                    setPushText(pushText);
                    setStatus(status);
                    setWaStatus(waStatus);
                    setEmailStatus(emailStatus);
                    setTelegramStatus(telegramStatus);
                    setPushStatus(pushStatus);
                    setOriginalFormData({
                        purpose,
                        smsTemplateId,
                        template,
                        waTemplateId,
                        waTemplate,
                        telegramTemplateId,
                        telegramTemplate,
                        emailSubject,
                        emailBody,
                        pushTitle,
                        pushText,
                        status,
                        waStatus,
                        emailStatus,
                        telegramStatus,
                        pushStatus,
                    });
                }
            })
            .catch((error) => {
                setResponseStatus(API_RESPONSE_TYPES.FAILURE);
                setResponseMessage(error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const onSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        setResponseStatus('');
        setResponseMessage('');
        const formData = {
            purpose,
            smsTemplateId,
            template,
            waTemplateId,
            waTemplate,
            telegramTemplateId,
            telegramTemplate,
            emailSubject,
            emailBody,
            pushTitle,
            pushText,
            status,
            waStatus,
            emailStatus,
            telegramStatus,
            pushStatus,
        };
        API.put(`/configurations/sms-templates/${id}`, formData)
            .then((response) => {
                const { status, message } = response.data;
                setResponseStatus(status);
                setResponseMessage(message);
                if (status === API_RESPONSE_TYPES.SUCCESS) {
                    setOriginalFormData(formData);
                    setTimeout(() => {
                        resetResponseData();
                        window.location.href = getReturnUrl(PERMISSION_TYPES.UPDATE);
                    }, 3000);
                }
            })
            .catch((error) => {
                setResponseStatus(API_RESPONSE_TYPES.FAILURE);
                setResponseMessage(error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const addText = (setMethod, value) => {
        setMethod((currentValue) => currentValue + value);
    };

    useEffect(() => {
        readData();
    }, [readData]);

    return (
        <>
            <PageHeading {...props} />
            <FormContainer>
                <Form method="POST" action="#" onSubmit={onSubmit}>
                    <Fieldset>
                        <Label required>Purpose</Label>
                        <TextInput
                            value={purpose}
                            onChange={setPurpose}
                            placeholder="Please enter purpose"
                            disabled={isLoading === true}
                        />
                    </Fieldset>
                    <Fieldset forLegend>
                        <Legend>SMS Template</Legend>
                        <Fieldset>
                            <Label>Template ID</Label>
                            <TextInput
                                value={smsTemplateId}
                                onChange={setSmsTemplateId}
                                placeholder="Please enter template id"
                                disabled={isLoading === true}
                            />
                        </Fieldset>
                        <Fieldset>
                            <Label required>Template</Label>
                            <TextInput
                                value={template}
                                onChange={setTemplate}
                                placeholder="Please enter template"
                                disabled={isLoading === true}
                            />
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setTemplate, '{{{FIRST_NAME}}}')}
                            >
                                First Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setTemplate, '{{{LAST_NAME}}}')}
                            >
                                Last Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setTemplate, '{{{NUMBER}}}')}
                            >
                                Number
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setTemplate, '{{{BALANCE}}}')}
                            >
                                Balance
                            </StyledMicroButton>
                        </Fieldset>
                        <Fieldset>
                            <Label required>Status</Label>
                            <Dropdown
                                value={status}
                                onChange={setStatus}
                                placeholder="Status"
                                disabled={isLoading === true}
                                options={[
                                    {
                                        title: '-- SELECT STATUS --',
                                        value: '',
                                    },
                                    {
                                        title: 'Active',
                                        value: 'Active',
                                    },
                                    {
                                        title: 'Inactive',
                                        value: 'Inactive',
                                    },
                                ]}
                            />
                        </Fieldset>
                    </Fieldset>

                    <Fieldset forLegend>
                        <Legend>Whatsapp Template</Legend>
                        <Fieldset>
                            <Label>Template ID</Label>
                            <TextInput
                                value={waTemplateId}
                                onChange={setWaTemplateId}
                                placeholder="Please enter template id"
                                disabled={isLoading === true}
                            />
                        </Fieldset>
                        <Fieldset>
                            <Label>Template</Label>
                            <TextInput
                                value={waTemplate}
                                onChange={setWaTemplate}
                                placeholder="Please enter template"
                                disabled={isLoading === true}
                            />
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setWaTemplate, '{{{FIRST_NAME}}}')}
                            >
                                First Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setWaTemplate, '{{{LAST_NAME}}}')}
                            >
                                Last Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setWaTemplate, '{{{NUMBER}}}')}
                            >
                                Number
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setWaTemplate, '{{{BALANCE}}}')}
                            >
                                Balance
                            </StyledMicroButton>
                        </Fieldset>
                        <Fieldset>
                            <Label required>Status</Label>
                            <Dropdown
                                value={waStatus}
                                onChange={setWaStatus}
                                placeholder="Status"
                                disabled={isLoading === true}
                                options={[
                                    {
                                        title: '-- SELECT STATUS --',
                                        value: '',
                                    },
                                    {
                                        title: 'Active',
                                        value: 'Active',
                                    },
                                    {
                                        title: 'Inactive',
                                        value: 'Inactive',
                                    },
                                ]}
                            />
                        </Fieldset>
                    </Fieldset>

                    <Fieldset forLegend>
                        <Legend>Telegram Template</Legend>
                        <Fieldset>
                            <Label>Template ID</Label>
                            <TextInput
                                value={telegramTemplateId}
                                onChange={setTelegramTemplateId}
                                placeholder="Please enter template id"
                                disabled={isLoading === true}
                            />
                        </Fieldset>
                        <Fieldset>
                            <Label>Template</Label>
                            <TextInput
                                value={telegramTemplate}
                                onChange={setTelegramTemplate}
                                placeholder="Please enter template"
                                disabled={isLoading === true}
                            />
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setTelegramTemplate, '{{{FIRST_NAME}}}')}
                            >
                                First Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setTelegramTemplate, '{{{LAST_NAME}}}')}
                            >
                                Last Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setTelegramTemplate, '{{{NUMBER}}}')}
                            >
                                Number
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setTelegramTemplate, '{{{BALANCE}}}')}
                            >
                                Balance
                            </StyledMicroButton>
                        </Fieldset>
                        <Fieldset>
                            <Label required>Status</Label>
                            <Dropdown
                                value={telegramStatus}
                                onChange={setTelegramStatus}
                                placeholder="Status"
                                disabled={isLoading === true}
                                options={[
                                    {
                                        title: '-- SELECT STATUS --',
                                        value: '',
                                    },
                                    {
                                        title: 'Active',
                                        value: 'Active',
                                    },
                                    {
                                        title: 'Inactive',
                                        value: 'Inactive',
                                    },
                                ]}
                            />
                        </Fieldset>
                    </Fieldset>

                    <Fieldset forLegend>
                        <Legend>Email Template</Legend>
                        <Fieldset>
                            <Label>Subject</Label>
                            <TextInput
                                value={emailSubject}
                                onChange={setEmailSubject}
                                placeholder="Please enter subject"
                                disabled={isLoading === true}
                            />
                        </Fieldset>
                        <Fieldset>
                            <Label>Body</Label>
                            <TextInput
                                value={emailBody}
                                onChange={setEmailBody}
                                placeholder="Please enter body"
                                disabled={isLoading === true}
                            />
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setEmailBody, '{{{FIRST_NAME}}}')}
                            >
                                First Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setEmailBody, '{{{LAST_NAME}}}')}
                            >
                                Last Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setEmailBody, '{{{NUMBER}}}')}
                            >
                                Number
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setEmailBody, '{{{BALANCE}}}')}
                            >
                                Balance
                            </StyledMicroButton>
                        </Fieldset>
                        <Fieldset>
                            <Label required>Status</Label>
                            <Dropdown
                                value={emailStatus}
                                onChange={setEmailStatus}
                                placeholder="Status"
                                disabled={isLoading === true}
                                options={[
                                    {
                                        title: '-- SELECT STATUS --',
                                        value: '',
                                    },
                                    {
                                        title: 'Active',
                                        value: 'Active',
                                    },
                                    {
                                        title: 'Inactive',
                                        value: 'Inactive',
                                    },
                                ]}
                            />
                        </Fieldset>
                    </Fieldset>

                    <Fieldset forLegend>
                        <Legend>Push Notification Template</Legend>
                        <Fieldset>
                            <Label>Title</Label>
                            <TextInput
                                value={pushTitle}
                                onChange={setPushTitle}
                                placeholder="Please enter title"
                                disabled={isLoading === true}
                            />
                        </Fieldset>
                        <Fieldset>
                            <Label>Notification Text</Label>
                            <TextInput
                                value={pushText}
                                onChange={setPushText}
                                placeholder="Please enter notification text"
                                disabled={isLoading === true}
                            />
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setPushText, '{{{FIRST_NAME}}}')}
                            >
                                First Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading === true}
                                type="button"
                                onClick={() => addText(setPushText, '{{{LAST_NAME}}}')}
                            >
                                Last Name
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setPushText, '{{{NUMBER}}}')}
                            >
                                Number
                            </StyledMicroButton>
                            <StyledMicroButton
                                disabled={isLoading}
                                type="button"
                                onClick={() => addText(setPushText, '{{{BALANCE}}}')}
                            >
                                Balance
                            </StyledMicroButton>
                        </Fieldset>
                        <Fieldset>
                            <Label required>Status</Label>
                            <Dropdown
                                value={pushStatus}
                                onChange={setPushStatus}
                                placeholder="Status"
                                disabled={isLoading === true}
                                options={[
                                    {
                                        title: '-- SELECT STATUS --',
                                        value: '',
                                    },
                                    {
                                        title: 'Active',
                                        value: 'Active',
                                    },
                                    {
                                        title: 'Inactive',
                                        value: 'Inactive',
                                    },
                                ]}
                            />
                        </Fieldset>
                    </Fieldset>

                    {responseStatus && (
                        <Fieldset>
                            <MessageBox status={responseStatus} message={responseMessage} />
                        </Fieldset>
                    )}
                    <Fieldset>
                        <UpdateButton disabled={isLoading === true} type="submit">
                            Update
                        </UpdateButton>
                        <ResetButton
                            disabled={isLoading === true}
                            type="button"
                            onClick={resetForm}
                        >
                            Reset
                        </ResetButton>
                    </Fieldset>
                </Form>
                <OverlayLoader showLoader={isLoading} />
            </FormContainer>
        </>
    );
};

export default withAppHOC(SmsTemplatesUpdate);
